import type { ValidationFormState } from '../types';

type Line = ValidationFormState['lines'][number];

/**
 * Regroupe les lignes de facture qui partagent la MÊME structure comptable :
 * produit Zoho + compte + taux de TVA (+ client, pour la sous-traitance).
 * Zoho n'a aucun besoin du détail ligne à ligne quand l'imputation est
 * identique — une seule ligne à la somme suffit et allège la facture.
 *
 * Ne SONT PAS regroupées (garde-fous demandés) :
 *   - deux produits ou deux comptes distincts ;
 *   - deux taux de TVA différents ;
 *   - une partie avec TVA et une partie sans (`vat_rate: null` reste une
 *     classe à part entière, distincte de 0 % / autoliquidation — la nature
 *     comptable n'est pas la même) ;
 *   - deux clients distincts (champ « Détails des clients » côté Books).
 */
function groupKey(l: Line): string {
  const product = l.zoho_item_id ?? l.zoho_item_name ?? '';
  const account = (l.account ?? '').trim().toLowerCase();
  const vat = l.vat_rate == null ? 'none' : String(l.vat_rate);
  const client = l.client_books_id ?? '';
  return [product, account, vat, client].join('|');
}

const round2 = (n: number) => Math.round(n * 100) / 100;

export interface ConsolidationResult {
  lines: Line[];
  /** Nombre de lignes supprimées par le regroupement (0 = rien n'a changé). */
  removed: number;
}

/**
 * Les montants sont sommés en HT (quantity × PU), arrondis au centime PAR
 * LIGNE avant sommation — même algorithme que Zoho et que le total affiché
 * dans `LineItemsTable`, pour éviter un écart d'un centime au regroupement.
 * La ligne consolidée porte quantity = 1 et PU = total du groupe.
 */
export function consolidateLines(lines: Line[]): ConsolidationResult {
  if (lines.length < 2) return { lines, removed: 0 };

  const order: string[] = [];
  const groups = new Map<string, Line[]>();
  for (const l of lines) {
    const k = groupKey(l);
    if (!groups.has(k)) {
      groups.set(k, []);
      order.push(k);
    }
    groups.get(k)!.push(l);
  }

  if (order.length === lines.length) return { lines, removed: 0 };

  const merged = order.map((k) => {
    const group = groups.get(k)!;
    if (group.length === 1) return group[0]!;

    const totalHt = round2(
      group.reduce((acc, l) => acc + round2(l.quantity * l.unit_price_ht), 0),
    );
    // Montant en devise d'origine : sommé de la même façon, pour que la
    // reconversion (changement manuel de taux) reste juste après regroupement.
    const origs = group.map((l) => l.original_unit_price_ht);
    const totalOriginal = origs.every((o) => o != null)
      ? round2(
          group.reduce(
            (acc, l) => acc + round2(l.quantity * (l.original_unit_price_ht ?? 0)),
            0,
          ),
        )
      : null;

    // Descriptions : on garde les libellés distincts non vides (utile pour les
    // lignes où l'utilisateur a saisi un détail), sinon vide comme d'habitude.
    const descriptions = [
      ...new Set(group.map((l) => (l.description ?? '').trim()).filter(Boolean)),
    ];

    return {
      ...group[0]!,
      description: descriptions.join(' + '),
      quantity: 1,
      unit_price_ht: totalHt,
      ...(totalOriginal != null ? { original_unit_price_ht: totalOriginal } : {}),
    };
  });

  return { lines: merged, removed: lines.length - merged.length };
}
